// ============================================================================
// Lead time disclaimer tooltip (OIE-356)
//
// Powers the "?" button rendered by snippets/lead-time.liquid when it is called with
// tooltip: true — currently the PDP, the cart page and the slide cart. Tiles do NOT get
// the button (their stock rows run down to 10px in the autocomplete panel); they carry
// the same copy on a native `title` instead.
//
// Two decisions worth knowing before changing anything here:
//
// 1. ONE tooltip element, appended to <body>. Not rendered inside the line it belongs
//    to. The cart drawer is its own stacking context and the PDP card clips, so a
//    tooltip positioned inside either would be cut off or trapped behind other UI.
//    `position: fixed` on a body-level node escapes both.
//
// 2. DELEGATED listeners on document, not per-button. Cart lines are replaced wholesale
//    by cart-items.js refresh() and by the drawer re-render, so any listener bound to a
//    button would die on the next cart update. Delegation survives that for free.
//
// The copy itself lives on the button's data-note, written by the Liquid snippet, so
// the disclaimer text has exactly one source per surface.
// ============================================================================
(function () {
  'use strict';

  // The drawer and the importer can both pull this script in twice.
  if (window.__bcsLeadTimeTooltipInit) return;
  window.__bcsLeadTimeTooltipInit = true;

  var TRIGGER = '[data-bcs-lead-time-info]';
  var GAP = 8;        // space between the button and the bubble
  var MARGIN = 8;     // keep this far from the viewport edge

  var tip = null;     // the single body-level bubble
  var openFor = null; // the button it is currently describing

  function ensureTip() {
    if (tip) return tip;
    tip = document.createElement('div');
    tip.className = 'bcs-lead-time-tooltip';
    tip.setAttribute('role', 'tooltip');
    tip.id = 'bcs-lead-time-tooltip';
    tip.hidden = true;
    document.body.appendChild(tip);
    return tip;
  }

  function position(btn) {
    // Anchor horizontally to the VISIBLE CONTENT — bullet through the "?" button — not
    // to the row's own box. .bcs-lead-time is `width: 100%` (needed so the line does not
    // stretch oddly inside its flex container), which means the row is routinely wider
    // than what is actually drawn inside it. Centring on the row's full width centred the
    // bubble on the ROW, not the text: on a wide container (the PDP delivery card) it
    // landed over "FREE SHIPPING" and "Add to cart" instead of over "Lead time: N days".
    //
    // The row's LEFT edge is still correct — the bullet (::before) is the first flex
    // item and sits flush with the row's own left edge. Only the RIGHT edge was wrong,
    // so it is replaced with the button's own right edge: the button is always the last
    // visible element in the row, regardless of how wide the row itself stretches.
    var row = btn.closest('[data-bcs-lead-time]') || btn;
    var rowRect = row.getBoundingClientRect();
    var btnRect = btn.getBoundingClientRect();
    var contentLeft = rowRect.left;
    var contentWidth = btnRect.right - contentLeft;

    // Measure before placing — width is content-dependent and capped by max-width.
    tip.style.left = '0px';
    tip.style.top = '0px';
    var w = tip.offsetWidth;
    var h = tip.offsetHeight;

    // Centre on the visible content, then clamp so it can never hang off either edge.
    // Without the clamp a lead time near the right-hand side of the cart drawer pushes
    // the bubble off-screen.
    var left = contentLeft + (contentWidth / 2) - (w / 2);
    left = Math.max(MARGIN, Math.min(left, window.innerWidth - w - MARGIN));

    // Vertical placement still comes from the ROW, not the button: when the row wraps to
    // two lines (the autocomplete panel), the row's bottom edge moves with it while the
    // button's own rect would not — using the button here would overlap the bubble with
    // the wrapped second line.
    var below = rowRect.bottom + GAP;
    var top = (below + h + MARGIN > window.innerHeight && rowRect.top - GAP - h > MARGIN)
      ? rowRect.top - GAP - h
      : below;

    tip.style.left = Math.round(left) + 'px';
    tip.style.top = Math.round(top) + 'px';
  }

  function close() {
    if (!openFor) return;
    openFor.setAttribute('aria-expanded', 'false');
    openFor.removeAttribute('aria-describedby');
    openFor = null;
    if (tip) tip.hidden = true;
  }

  function open(btn) {
    ensureTip();
    tip.textContent = btn.getAttribute('data-note') || '';
    tip.hidden = false;
    openFor = btn;
    btn.setAttribute('aria-expanded', 'true');
    btn.setAttribute('aria-describedby', tip.id);
    position(btn);
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest ? e.target.closest(TRIGGER) : null;

    if (btn) {
      // Product cards and cart lines wrap their contents in a link, and the cart row
      // has its own click handling. Without both of these, tapping the "?" navigates to
      // the product instead of opening the tooltip.
      e.preventDefault();
      e.stopPropagation();
      if (openFor === btn) close(); else open(btn);
      return;
    }

    // Any click elsewhere dismisses — including inside the bubble, which has nothing
    // interactive in it.
    close();
  });

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && openFor) {
      var btn = openFor;
      close();
      btn.focus(); // return focus where the user left it
    }
  });

  // The bubble is position: fixed against a trigger that scrolls, so it would drift
  // away from its button. Cheaper and less surprising to just close it.
  window.addEventListener('scroll', close, true);
  window.addEventListener('resize', close);

  // A cart update replaces the row the open button lived in, orphaning the bubble.
  document.addEventListener('on:cart:change', close);
  document.addEventListener('on:line-item:change', close);
})();
